import { useEffect, useState } from 'react';
import { getOptimizedImageUrl } from '../lib/utils';

export default function Lightbox({ images, currentIndex, onClose, onPrev, onNext }) {
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Minimum swipe distance in pixels
  const minSwipeDistance = 50;

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };

    // Prevent body scroll when lightbox is open
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, onPrev, onNext]);

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      onNext();
    } else if (isRightSwipe) {
      onPrev();
    }
  };

  if (currentIndex === null || !images || images.length === 0) return null;

  const currentImage = images[currentIndex];

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/95 backdrop-blur-sm transition-opacity duration-300"
      onClick={onClose}
    >
      {/* Top Bar */}
      <div className="absolute top-0 inset-x-0 flex items-center justify-between p-4 text-white z-10 bg-gradient-to-b from-black/60 to-transparent">
        <span className="text-sm font-medium tracking-wider">
          {currentIndex + 1} / {images.length}
        </span>
        <button
          onClick={onClose}
          className="p-2 text-slate-300 hover:text-white transition-colors focus:outline-none"
          aria-label="Luk visning"
        >
          <i className="fa-regular fa-xmark text-2xl" />
        </button>
      </div>

      {/* Main Content Area */}
      <div
        className="relative w-full max-w-5xl px-4 flex items-center justify-center h-[75vh]"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Prev Button */}
        <button
          onClick={onPrev}
          className="absolute left-4 md:left-8 z-10 p-3 rounded-full bg-black/40 text-slate-300 hover:text-white hover:bg-black/60 transition-all focus:outline-none"
          aria-label="Forrige billede"
        >
          <i className="fa-regular fa-chevron-left text-xl md:text-2xl" />
        </button>

        {/* Image Container */}
        <div className="relative max-h-full max-w-full flex flex-col items-center">
          <img
            src={getOptimizedImageUrl(currentImage.image[0], 1600, 95)}
            alt={currentImage.title}
            className="max-h-[70vh] max-w-full object-contain rounded shadow-2xl select-none"
          />
          {currentImage.title && (
            <div className="mt-4 text-center text-white px-4">
              <h3 className="text-base md:text-lg font-semibold tracking-wide">
                {currentImage.title}
              </h3>
              {currentImage.category && (
                <span className="inline-block mt-1 text-xs text-emerald-400 bg-emerald-950/50 px-2.5 py-0.5 rounded-full border border-emerald-800/30">
                  {currentImage.category}
                </span>
              )}
              {currentImage.description && (
                <p className="mt-2 text-xs md:text-sm text-slate-300 max-w-2xl mx-auto line-clamp-2">
                  {currentImage.description}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Next Button */}
        <button
          onClick={onNext}
          className="absolute right-4 md:right-8 z-10 p-3 rounded-full bg-black/40 text-slate-300 hover:text-white hover:bg-black/60 transition-all focus:outline-none"
          aria-label="Næste billede"
        >
          <i className="fa-regular fa-chevron-right text-xl md:text-2xl" />
        </button>
      </div>

      {/* Bottom Swipe Indicator for Mobile */}
      <div className="absolute bottom-4 text-xs text-slate-500 md:hidden">
        <i className="fa-regular fa-arrows-left-right mr-1.5" />
        Stryg til siden for at skifte billede
      </div>
    </div>
  );
}
